/**
 * Calendar export page.
 *
 * Two ways out of the app plus a visual: the ICS file for people who keep a
 * calendar, a plain text summary for the group chat, and the timeline card
 * itself for a screenshot. The ICS path comes from the server so the downloaded
 * file and the rendered timeline are the same projection.
 */

import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { CalendarViewResponse } from '@shared/types'

import { Timeline } from '../../components/calendar/timeline'
import { PageHeader } from '../../components/layout/page-header'
import { ErrorAlert, ErrorBlock, SkeletonCard } from '../../components/layout/states'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { useCalendarView } from '../../hooks/use-shared'
import { copyText } from '../../lib/clipboard'
import { formatLocalDateTime } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { useSharedOutlet } from './shared-layout'

type CopyState = 'idle' | 'copied' | 'failed'

/** Characters a file name may not carry on the platforms people download to. */
const UNSAFE_FILE_NAME = /[\\/:*?"<>|\u0000-\u001f]/g

/**
 * Name the downloaded calendar file after the outing.
 *
 * The `download` attribute has to be spelled out: without it the browser names the
 * file after the URL, which is `calendar.ics` for everybody. The share token stays
 * out of it, because a file name travels through downloads folders and chat apps.
 */
export function icsFileName(title: string, startsAt: string, eventId: string): string {
  const cleaned = title.replace(UNSAFE_FILE_NAME, '-').replace(/\s+/g, ' ').trim().slice(0, 60)
  const date = startsAt.slice(0, 10)
  const stem = cleaned === '' ? eventId : cleaned
  return date === '' ? `${stem}.ics` : `${stem}-${date}.ics`
}

export function CalendarExportPage() {
  const { t } = useTranslation()
  const { shareToken, event } = useSharedOutlet()
  const calendarQuery = useCalendarView(shareToken)
  const [copyState, setCopyState] = useState<CopyState>('idle')

  const locale = currentLocale()

  /** Plain text summary: one line per segment, ready for a chat message. */
  function buildSummary(view: CalendarViewResponse): string {
    const lines = [
      view.event.title,
      formatLocalDateTime(view.event.eventDateStart, locale),
      ...view.timeline.map(
        (entry) =>
          `${entry.startAt.slice(11, 16)}-${entry.endAt.slice(11, 16)} ${t(entry.labelKey)}`,
      ),
    ]
    if (view.decided) {
      lines.push(view.decided.districtName)
    }
    return lines.join('\n')
  }

  /**
   * Copies the summary through the shared clipboard helper.
   *
   * The helper already owns the fallback for an insecure origin or a blocked
   * clipboard, so the page only has to render the outcome.
   */
  async function copySummary(view: CalendarViewResponse): Promise<void> {
    setCopyState((await copyText(buildSummary(view))) ? 'copied' : 'failed')
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: event.title, to: `/e/${shareToken}` },
          { label: t('calendar.title'), to: `/e/${shareToken}/calendar` },
          { label: t('calendar.export') },
        ]}
        title={t('calendar.export')}
      />

      {calendarQuery.isPending ? (
        <SkeletonCard />
      ) : calendarQuery.isError || !calendarQuery.data ? (
        <ErrorBlock
          title={t('error.internal_error')}
          onRetry={() => void calendarQuery.refetch()}
          retryLabel={t('common.retry')}
        />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{t('calendar.exportIcs')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <p className="text-muted-foreground text-sm">{t('calendar.exportIcsHint')}</p>
              <Button asChild className="self-start">
                <a
                  href={calendarQuery.data.icsPath}
                  download={icsFileName(
                    calendarQuery.data.event.title,
                    calendarQuery.data.event.eventDateStart,
                    calendarQuery.data.event.id,
                  )}
                >
                  {t('calendar.exportIcs')}
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('calendar.copySummary')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <p className="text-muted-foreground text-sm">{t('calendar.copySummaryHint')}</p>
              <Button
                variant="outline"
                className="self-start"
                onClick={() => void copySummary(calendarQuery.data)}
              >
                {t('calendar.copySummary')}
              </Button>
              <p className="text-muted-foreground text-xs">{t('calendar.copyHint')}</p>
              {copyState === 'copied' ? (
                <Alert variant="success">
                  <AlertDescription>{t('common.copied')}</AlertDescription>
                </Alert>
              ) : null}
              {copyState === 'failed' ? (
                <ErrorAlert message={t('export.copyFailed')} />
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('calendar.copyImage')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <p className="text-muted-foreground text-sm">{t('export.imageHint')}</p>
              <div className="border-border rounded-lg border p-3">
                <Timeline
                  startAt={calendarQuery.data.event.eventDateStart}
                  endAt={calendarQuery.data.event.eventDateEnd}
                  entries={calendarQuery.data.timeline}
                />
              </div>
            </CardContent>
          </Card>

          <Button variant="ghost" size="sm" className="self-start" asChild>
            <Link to={`/e/${shareToken}/calendar`}>{t('calendar.title')}</Link>
          </Button>
        </>
      )}
    </div>
  )
}
